import React, { type FC } from 'react';

import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';

import type { templateDialog_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar$key } from '@atlassian/jira-relay/src/__generated__/templateDialog_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar.graphql';
import { INTENT_TEMPLATE_TYPES, type IntentTemplateType } from '../../../../common/types';

import { TemplateIcon } from '../../../../common/ui/template-icon';
import { TemplateLozenge } from '../../../../common/ui/template-lozenge';
import messages from './messages';

const templateTypeDescriptions: Record<IntentTemplateType, MessageDescriptor> = {
	DISCOVERED: messages.fromYourDataDescription,
	SHARED: messages.catalogTemplateDescription,
	STANDARD: messages.catalogTemplateDescription,
};

export const TemplateDialog: FC<{
	intentTemplateDataRef: templateDialog_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar$key;
}> = ({ intentTemplateDataRef }) => {
	const { formatMessage } = useIntl();

	const intentTemplateData = useFragment(
		graphql`
			fragment templateDialog_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar on VirtualAgentIntentTemplate {
				name
				type
				description
				noOfQuestions
			}
		`,
		intentTemplateDataRef,
	);

	const templateType =
		intentTemplateData.type in INTENT_TEMPLATE_TYPES
			? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(intentTemplateData.type as IntentTemplateType)
			: undefined;

	return (
		<>
			<HeadingContainer>
				<TemplateIcon type={templateType} />
				<Heading size="xsmall">{intentTemplateData.name}</Heading>
			</HeadingContainer>
			<ContentContainer>
				<SectionContainer>
					<TemplateLozenge type={templateType} />
					{templateType != null && formatMessage(templateTypeDescriptions[templateType])}
				</SectionContainer>
				<SectionContainer>
					<Heading level="h200">{formatMessage(messages.descriptionLabel)}</Heading>
					{intentTemplateData.description}
				</SectionContainer>
				<SectionContainer>
					<Heading level="h200">{formatMessage(messages.summaryLabel)}</Heading>
					<TrainingPhrasesContainer>
						{formatMessage(messages.trainingPhrasesLabel)}
						<p>
							{formatMessage(messages.trainingPhrasesNumber, {
								numberOfTrainingPhrases: intentTemplateData.noOfQuestions,
							})}
						</p>
					</TrainingPhrasesContainer>
				</SectionContainer>
			</ContentContainer>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	padding: `${token('space.200', '16px')} ${token('space.300', '24px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border', colors.N40)}`,
	display: 'flex',
	gap: token('space.150', '12px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	padding: `${token('space.200', '16px')} ${token('space.300', '24px')}`,
	display: 'flex',
	gap: token('space.200', '16px'),
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TrainingPhrasesContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	width: '100%',
});
